/**
 * sk-plant.js — Pflanzen-Produktseite (templates/product.pflanze.json)
 * 1) Größenwechsel → Lieferform, Lieferzeit, Stück/m in der Kaufbox + markierte Zeile im Heckenrechner
 * 2) Heckenrechner: Heckenlänge → Stückzahl und Preis je Größe, Menge im Warenkorb-Formular
 * 4) Standort-Check Zimmerpflanzen: Fensterrichtung × Abstand → Lichtstufe → Ampel
 * 3) Texte je Größe ([data-sk-vmap="<json-id>"], z. B. Sichtschutz „in X Jahren 1,80 m", Übertopf,
 *    Endhöhe je Unterlage): JSON {"id:<variant-id>": text, "t:<Optionswert>": text}
 * 5) Beetrechner (sections/sk-plant-calc, gleiche Hooks wie der Heckenrechner): Fläche aus Länge × Breite,
 *    Pflanzdichte locker/dicht, Tuff-Rundung (data-sk-round-to / data-sk-round-below)
 * 6) Baum-Check (sections/sk-plant-tree): Abstand → Ampel; markierte Größe in der Größen-Tabelle ([data-sk-vrow])
 * 7) Flächenrechner Rollrasen (snippets/sk-plant-area, Teil „calc"): m² → Bestellmenge, Paletten, Preis inkl.
 *    Anlieferung, Wunschtermin; legt per /cart/add.js mit Eigenschaften in den Warenkorb
 * 8) Mehrere Lieferformen (13.10.2026): Ereignis „sk-plant:variant" aus der Mengen-View (sk-plant-qm.js) → Lieferblock,
 *    Icon-Steckbrief ([data-sk-vmap], Lieferform-Icon [data-sk-vswitch]/[data-sk-vcase]); „sk-plant:tab" → Heckenrechner
 *    zeigt nur die Größen der gewählten Lieferform ([data-sk-tab])
 * 9) Variantenbild (13.10.2026, Umbau 14.10.2026, snippets/sk-plant-media): vorn bleibt beim Größenwechsel
 *    (Empire-„variant-change", Listview, „sk-plant:variant" aus der Mengen-View) immer das KI-Standardbild der Größe –
 *    Bild, Vorschaubild, Zoom, KI-Label; Empire würde sonst das Lieferantenfoto (variant.featured_media) vorn einsetzen.
 *    Lieferantenfotos sind feste Zusatzfolien am Galerie-Ende (Liquid). Nur wenn das JSON [data-sk-plant-media] existiert.
 * 10) Auf-/Zuklappen (UX-Umbau 21.10.2026): „Mehr anzeigen“ im Steckbrief ([data-sk-more] → .is-all), „Weiterlesen“ der
 *    Beschreibung ([data-sk-clamp-btn] → .is-open; kurze Texte ohne Button), mobile Aufklapper der Sections
 *    ([data-sk-macc] + [data-sk-macc-btn] → .is-open, ab 768 px immer offen per CSS), Links auf #sk-plant-hedge öffnen
 *    den Heckenrechner-Aufklapper an der Größenliste.
 * Kein Framework, keine Abhängigkeiten; Warenkorb-AJAX übernimmt assets/sk-xsell.js bzw. sk-plant-qm.js.
 */
(function () {
  'use strict';
  if (window.__skPlantInit) return;
  window.__skPlantInit = true;

  /** @type {Record<string, {id:number, form:string, spm:number|null, spmText:string|null, time:string|null}>} */
  let variants = {};
  const byId = {};

  function readVariants() {
    const el = document.querySelector('[data-sk-plant-variants]');
    if (!el) return;
    try {
      variants = JSON.parse(el.textContent);
    } catch (e) {
      variants = {};
    }
    for (const key of Object.keys(variants)) byId[String(variants[key].id)] = variants[key];
  }

  function setText(el, text) {
    if (!el) return;
    if (text) {
      el.textContent = text;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  function applyVariant(v) {
    if (!v) return;
    const box = document.querySelector('[data-sk-plant-delivery]');
    // Mengen-View: Lieferblock ist Größen-unabhängig (Stück/m-Spanne fest, Lieferform folgt dem Reiter) – 14.10.2026
    if (box && !box.hasAttribute('data-sk-plant-qm')) {
      const formRow = box.querySelector('[data-sk-plant-form-row]');
      const formEl = box.querySelector('[data-sk-plant-form]');
      if (formEl) formEl.textContent = v.form || '';
      if (formRow) formRow.hidden = !v.form;
      setText(box.querySelector('[data-sk-plant-time]'), v.time);
      const spmRow = box.querySelector('[data-sk-plant-spm-row]');
      const spmEl = box.querySelector('[data-sk-plant-spm]');
      if (spmEl && v.spmText) spmEl.textContent = v.spmText;
      if (spmRow) spmRow.hidden = !v.spmText;
    }
    for (const row of document.querySelectorAll('[data-sk-hedge-row]')) {
      const on = row.getAttribute('data-variant-id') === String(v.id);
      row.classList.toggle('is-selected', on);
      const badge = row.querySelector('[data-sk-hedge-badge]');
      if (badge) badge.hidden = !on;
    }
    markVrows(v.id);
  }

  /* Größen-Tabellen (z. B. Baum-Check): gewählte Größe markieren */
  function markVrows(id) {
    for (const row of document.querySelectorAll('[data-sk-vrow]')) {
      row.classList.toggle('is-selected', row.getAttribute('data-variant-id') === String(id));
    }
  }

  /* ---------- Texte je Größe (data-sk-vmap) ---------- */
  const vmapCache = {};
  function readMap(id) {
    if (!(id in vmapCache)) {
      const src = document.getElementById(id);
      try {
        vmapCache[id] = src ? JSON.parse(src.textContent) : null;
      } catch (e) {
        vmapCache[id] = null;
      }
    }
    return vmapCache[id];
  }
  /* Icon je Größe (z. B. Lieferform Topf/Ballen): [data-sk-vswitch="<json-id>"] > [data-sk-vcase="<code>"] */
  function applyVswitch(key) {
    for (const el of document.querySelectorAll('[data-sk-vswitch]')) {
      const map = readMap(el.getAttribute('data-sk-vswitch'));
      if (!map || !(key in map)) continue;
      for (const c of el.querySelectorAll('[data-sk-vcase]')) c.hidden = c.getAttribute('data-sk-vcase') !== map[key];
    }
  }
  function applyVmaps(key) {
    applyVswitch(key);
    for (const el of document.querySelectorAll('[data-sk-vmap]')) {
      const id = el.getAttribute('data-sk-vmap');
      if (!(id in vmapCache)) {
        const src = document.getElementById(id);
        try {
          vmapCache[id] = src ? JSON.parse(src.textContent) : null;
        } catch (e) {
          vmapCache[id] = null;
        }
      }
      const map = vmapCache[id];
      if (!map || !(key in map)) continue;
      const text = map[key];
      if (text) {
        el.textContent = text;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
      // optionale Zeile (z. B. <li> mit Icon) mit derselben ID ein-/ausblenden
      for (const row of document.querySelectorAll('[data-sk-vmap-row="' + id + '"]')) row.hidden = !text;
    }
  }

  /* Mengen-View (sk-plant-qm) auf der Seite? Dann gibt es keine einzelne „gewählte Größe". */
  function qmMode() {
    return !!document.querySelector('[data-sk-qm]');
  }

  /* ---------- Größenwechsel ---------- */
  function initVariantSync() {
    readVariants();
    // Mengen-View (auch Entwurfs-Vorschau ohne Empire-Produktdaten): gewählte Größe → Lieferblock + Steckbrief
    document.addEventListener('sk-plant:variant', function (e) {
      const id = e.detail && e.detail.id;
      if (!id) return;
      // Preis oben: „Ab <kleinster Preis>" (snippets/product-price, Pflanzen-Templates) → Preis der gewählten Größe;
      // in der Mengen-View bleibt „Ab …" stehen (mehrere Größen gleichzeitig wählbar, Summe steht unten)
      if (e.detail.priceText && !qmMode()) {
        const scope = (e.detail.sectionId && document.getElementById('shopify-section-' + e.detail.sectionId)) || document;
        for (const from of scope.querySelectorAll('[data-sk-price-from]')) {
          const label = from.querySelector('.price--label');
          if (label) label.hidden = true;
          const p = from.querySelector('[data-price]');
          if (p) p.textContent = e.detail.priceText;
        }
      }
      if (byId[String(id)]) applyVariant(byId[String(id)]);
      else markVrows(id);
      applyVmaps('id:' + id);
    });
    // Reiter der Mengen-View → Heckenrechner zeigt nur Größen dieser Lieferform; Lieferform-Zeile im Lieferblock folgt dem Reiter
    document.addEventListener('sk-plant:tab', function (e) {
      const i = e.detail ? String(e.detail.index) : '';
      const label = (e.detail && e.detail.label) || '';
      const box = document.querySelector('[data-sk-plant-delivery][data-sk-plant-qm]');
      if (box && label) {
        const key = Object.keys(variants).find(function (k) { return k === label || k.indexOf(label + ' / ') === 0; });
        const v = key ? variants[key] : null;
        const formRow = box.querySelector('[data-sk-plant-form-row]');
        const formEl = box.querySelector('[data-sk-plant-form]');
        if (v && formEl) formEl.textContent = v.form || '';
        if (v && formRow) formRow.hidden = !v.form;
      }
      for (const row of document.querySelectorAll('[data-sk-hedge-row][data-sk-tab]')) {
        row.classList.toggle('is-off-tab', row.getAttribute('data-sk-tab') !== i);
      }
      for (const el of document.querySelectorAll('[data-sk-hedge-tabname]')) el.textContent = (e.detail && e.detail.label) || '';
    });
    const hasVmaps = !!document.querySelector('[data-sk-vmap]');
    if (!Object.keys(variants).length && !hasVmaps) return;
    // Listview (options-listview-bundle): Radio-Value = Optionswert = Variantentitel (eine Option)
    document.addEventListener('change', function (e) {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || t.type !== 'radio' || !t.matches('[data-variant-option-value]')) return;
      if (!t.checked) return;
      const v = variants[t.value];
      applyVariant(v);
      if (!v) {
        const vid = t.getAttribute('data-variant-id') || (t.closest('[data-variant-id]') || { getAttribute: function () { return null; } }).getAttribute('data-variant-id');
        if (vid) markVrows(vid);
      }
      applyVmaps(v ? 'id:' + v.id : 't:' + t.value);
    });
    // Empire <variant-selection> feuert 'variant-change' (bubbelt nicht) → direkt abonnieren
    for (const vs of document.querySelectorAll('variant-selection')) {
      vs.addEventListener('variant-change', function (e) {
        const variant = e.detail && e.detail.variant;
        if (!variant) return;
        // „Ab"-Preis nur ausblenden, wenn wirklich eine andere Größe gewählt wurde (Empire meldet beim Laden die Startvariante)
        for (const f of document.querySelectorAll('[data-sk-price-from]')) {
          const l = f.querySelector('.price--label');
          if (qmMode() && f.getAttribute('data-min')) {
            // Mengen-View: „Ab <kleinster Preis>" bleibt – Empire schreibt den Preis der gemeldeten Größe, danach zurücksetzen
            const pq = f.querySelector('[data-price]');
            if (l) l.hidden = false;
            window.requestAnimationFrame(function () { if (pq) pq.textContent = f.getAttribute('data-min'); });
            continue;
          }
          if (f.getAttribute('data-variant') !== String(variant.id)) {
            if (l) l.hidden = true;
          } else if (l && !l.hidden && f.getAttribute('data-min')) {
            // Startvariante: Empire schreibt ggf. deren Preis → „Ab" bleibt beim kleinsten Preis
            const p = f.querySelector('[data-price]');
            window.requestAnimationFrame(function () { if (p) p.textContent = f.getAttribute('data-min'); });
          }
        }
        if (byId[String(variant.id)]) applyVariant(byId[String(variant.id)]);
        else markVrows(variant.id);
        applyVmaps('id:' + variant.id);
      });
    }
  }

  /* ---------- Heckenrechner ---------- */
  function initHedge(root) {
    const input = root.querySelector('[data-sk-hedge-len]');
    if (!input) return;
    const currency = root.getAttribute('data-currency') || 'EUR';
    const locale = (root.getAttribute('data-locale') || document.documentElement.lang || 'de').replace('_', '-');
    const ariaTpl = root.getAttribute('data-add-aria') || '';
    const perMTpl = root.getAttribute('data-per-m') || '';
    let fmt;
    try {
      fmt = new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
    } catch (e) {
      fmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    }
    const rows = Array.from(root.querySelectorAll('[data-sk-hedge-row]'));
    const roundTo = parseInt(root.getAttribute('data-sk-round-to'), 10) || 1;
    const roundBelow = parseFloat(root.getAttribute('data-sk-round-below')) || 0;

    function lengthValue() {
      const raw = String(input.value).replace(',', '.');
      const n = parseFloat(raw);
      if (!isFinite(n) || n <= 0) return 0;
      return Math.min(500, n);
    }

    function update() {
      const len = lengthValue();
      for (const row of rows) {
        const spm = parseFloat(row.getAttribute('data-spm'));
        const price = parseInt(row.getAttribute('data-price'), 10);
        // kleine Toleranz gegen Fließkomma-Artefakte (10 × 2.5 = 25.000000004)
        let count = len > 0 ? Math.max(1, Math.ceil(len * spm - 1e-9)) : 0;
        // Beetrechner: kleine Flächen auf ganze Tuffs (z. B. 3er) aufrunden
        if (roundTo > 1 && len > 0 && len < roundBelow && count % roundTo) count += roundTo - (count % roundTo);
        const countEl = row.querySelector('[data-sk-hedge-count]');
        const totalEl = row.querySelector('[data-sk-hedge-total]');
        const qtyEl = row.querySelector('[data-sk-hedge-qty]');
        const btn = row.querySelector('[data-sk-hedge-add]');
        if (countEl) countEl.textContent = String(count);
        if (totalEl) totalEl.textContent = fmt.format((price * count) / 100);
        const perMEl = row.querySelector('[data-sk-hedge-perm]');
        if (perMEl && perMTpl && len > 0) perMEl.textContent = perMTpl.replace('%PRICE%', fmt.format((price * count) / 100 / len));
        if (qtyEl) qtyEl.value = String(Math.max(1, count));
        if (btn) {
          btn.disabled = count < 1;
          if (ariaTpl) {
            btn.setAttribute('aria-label', ariaTpl.replace('%COUNT%', String(count)).replace('%SIZE%', row.getAttribute('data-title') || ''));
          }
        }
      }
    }

    input.addEventListener('input', update);
    input.addEventListener('change', function () {
      const len = lengthValue();
      if (len <= 0) input.value = '1';
      update();
    });
    for (const btn of root.querySelectorAll('[data-sk-hedge-step]')) {
      btn.addEventListener('click', function () {
        const step = parseFloat(btn.getAttribute('data-sk-hedge-step')) || 0;
        const next = Math.min(500, Math.max(1, Math.round((lengthValue() + step) * 2) / 2));
        input.value = String(next);
        update();
      });
    }
    update();
  }

  /* ---------- Standort-Check (Zimmerpflanzen, sections/sk-plant-indoor) ----------
   * Lichtstufe 1–5 je Fensterrichtung × Abstand (Faustregel, Methodik: typen/F6-indoor-topfpflanze.md Kap. 16):
   * 5 volle Sonne · 4 hell mit etwas direkter Sonne · 3 hell ohne direkte Sonne · 2 halbschattig · 1 schattig */
  const LIGHT = {
    sued: [5, 4, 3, 2],
    dach: [5, 4, 3, 2],
    west: [4, 3, 2, 1],
    ost: [4, 3, 2, 1],
    nord: [3, 2, 1, 1]
  };
  function initSite(root) {
    const min = parseInt(root.getAttribute('data-min'), 10);
    const max = parseInt(root.getAttribute('data-max'), 10);
    const result = root.querySelector('[data-sk-site-result]');
    const textEl = root.querySelector('[data-sk-site-text]');
    let texts = {};
    try {
      texts = JSON.parse(root.querySelector('[data-sk-site-texts]').textContent);
    } catch (e) {
      return;
    }
    function update() {
      const dir = root.querySelector('[data-sk-site-dir]:checked');
      const dist = root.querySelector('[data-sk-site-dist]:checked');
      if (!dir || !dist || !LIGHT[dir.value]) return;
      const level = LIGHT[dir.value][Math.min(4, Math.max(1, parseInt(dist.value, 10))) - 1];
      let state = 'ok';
      // volle Sonne (Stufe 5) über dem Bedarf verbrennt Blätter → immer rot; sonst 1 Stufe Abweichung = gelb
      if (level > max) state = level === 5 || level - max > 1 ? 'too_bright' : 'bright';
      else if (level < min) state = min - level === 1 ? 'dim' : 'too_dark';
      const here = (texts.here || '').replace('%LEVEL%', String(level)).replace('%TEXT%', (texts.level || [])[level - 1] || '');
      result.setAttribute('data-state', state);
      textEl.textContent = here + ' ' + (texts[state] || '');
    }
    root.addEventListener('change', update);
  }

  /* ---------- Beetrechner (sections/sk-plant-calc) ----------
   * Rechnen/Warenkorb über initHedge (gleiche Hooks); hier nur Länge × Breite → Fläche und Dichte locker/dicht */
  function initCalc(root) {
    const area = root.querySelector('[data-sk-hedge-len]');
    if (!area) return;
    const fire = function () { area.dispatchEvent(new Event('input', { bubbles: true })); };
    const l = root.querySelector('[data-sk-calc-l]');
    const b = root.querySelector('[data-sk-calc-b]');
    function fromDims() {
      const lv = parseFloat(String(l.value).replace(',', '.'));
      const bv = parseFloat(String(b.value).replace(',', '.'));
      if (isFinite(lv) && isFinite(bv) && lv > 0 && bv > 0) {
        area.value = String(Math.min(500, Math.round(lv * bv * 10) / 10));
        fire();
      }
    }
    if (l && b) { l.addEventListener('input', fromDims); b.addEventListener('input', fromDims); }
    const tpl = root.getAttribute('data-dens-label') || '';
    const locale = (root.getAttribute('data-locale') || document.documentElement.lang || 'de').replace('_', '-');
    let nf;
    try { nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }); } catch (e) { nf = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }); }
    for (const r of root.querySelectorAll('[data-sk-calc-dens]')) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        const d = parseFloat(r.value) || 0;
        if (d <= 0) return;
        for (const row of root.querySelectorAll('[data-sk-hedge-row]')) row.setAttribute('data-spm', String(d));
        const label = tpl.replace('%N%', nf.format(d));
        for (const el of root.querySelectorAll('[data-sk-calc-dens-label]')) el.textContent = label;
        fire();
      });
    }
  }

  /* ---------- Baum-Check (sections/sk-plant-tree): Abstand → Ampel ----------
   * Radius = halbe Endbreite; passt ab Radius + 0,5 m, knapp ab Radius, sonst zu eng (F1 Kap. 2 „platz") */
  function initTreeCheck(root) {
    const input = root.querySelector('[data-sk-tree-dist]');
    const result = root.querySelector('[data-sk-tree-result]');
    const text = root.querySelector('[data-sk-tree-text]');
    let texts = {};
    try { texts = JSON.parse(root.querySelector('[data-sk-tree-texts]').textContent); } catch (e) { return; }
    const dec = document.createElement('textarea');
    for (const k of Object.keys(texts)) { dec.innerHTML = texts[k]; texts[k] = dec.value; }
    const radius = parseFloat(root.getAttribute('data-radius')) || 0;
    if (!input || !result || !text || radius <= 0) return;
    function update() {
      const raw = String(input.value).trim();
      const d = parseFloat(raw.replace(',', '.'));
      let state = 'idle';
      if (raw !== '' && isFinite(d) && d >= 0) {
        if (d >= radius + 0.5 - 1e-9) state = 'ok';
        else if (d >= radius - 1e-9) state = 'tight';
        else state = 'small';
      }
      result.setAttribute('data-state', state);
      text.textContent = texts[state] || '';
    }
    input.addEventListener('input', update);
    update();
  }

  /* ---------- Flächenrechner Rollrasen (snippets/sk-plant-area, Teil „calc") ---------- */
  function initArea(root) {
    const $ = function (sel) { return root.querySelector(sel); };
    const num = function (a, d) { const n = parseFloat(root.getAttribute(a)); return isFinite(n) ? n : d; };
    const price = num('data-price', 0);
    const minM2 = num('data-min', 1);
    const wastePct = num('data-waste', 0);
    const pallet = num('data-pallet', 0);
    const kg = num('data-kg', 0);
    const del = num('data-del', 0);
    const delExtra = num('data-del-extra', 0);
    const lead = num('data-lead', 3);
    const days = (root.getAttribute('data-days') || '').split(',').map(Number).filter(Boolean);
    const months = (root.getAttribute('data-months') || '').split(',').map(Number).filter(Boolean);
    const variantId = Number(root.getAttribute('data-variant-id'));
    const locale = (root.getAttribute('data-locale') || document.documentElement.lang || 'de').replace('_', '-');
    const currency = root.getAttribute('data-currency') || 'EUR';
    let fmt, nf;
    try { fmt = new Intl.NumberFormat(locale, { style: 'currency', currency: currency }); } catch (e) { fmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }); }
    try { nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }); } catch (e) { nf = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }); }
    let i18n = {};
    try { i18n = JSON.parse($('[data-sk-area-i18n]').textContent); } catch (e) { i18n = {}; }
    const dec = document.createElement('textarea');
    for (const k of Object.keys(i18n)) { if (typeof i18n[k] === 'string') { dec.innerHTML = i18n[k]; i18n[k] = dec.value; } }

    const m2 = $('[data-sk-area-m2]');
    const wasteEl = $('[data-sk-area-waste]');
    const plz = $('[data-sk-area-plz]');
    const dateSel = $('[data-sk-area-date]');
    const qty = $('[data-sk-area-qty]');
    const form = $('[data-sk-area-form]');
    const btn = $('[data-sk-area-atc]');
    const btnText = $('[data-sk-area-atc-text]');
    const msg = $('[data-sk-area-msg]');
    const minNote = $('[data-sk-area-min-note]');
    const l = $('[data-sk-area-l]');
    const b = $('[data-sk-area-b]');
    if (!m2 || !form || !btn) return;
    let state = { area: 0, order: 0 };

    function areaValue() {
      const n = parseFloat(String(m2.value).replace(',', '.'));
      return isFinite(n) && n > 0 ? Math.min(5000, n) : 0;
    }
    function setText(sel, t) { const el = $(sel); if (el) el.textContent = t; }
    function update() {
      const a = areaValue();
      const withWaste = wasteEl && wasteEl.checked ? a * (1 + wastePct / 100) : a;
      const need = a > 0 ? Math.ceil(withWaste - 1e-9) : 0;
      const order = a > 0 ? Math.max(minM2, need) : 0;
      const pal = order > 0 ? (pallet > 0 ? Math.max(1, Math.ceil(order / pallet)) : 1) : 0;
      const goods = price * order;
      const delivery = order > 0 ? del + Math.max(0, pal - 1) * delExtra : 0;
      const total = goods + delivery;
      state = { area: a, order: order };
      setText('[data-sk-area-order]', String(order));
      setText('[data-sk-area-pallets]', String(pal));
      setText('[data-sk-area-weight]', nf.format(order * kg));
      setText('[data-sk-area-goods]', fmt.format(goods / 100));
      setText('[data-sk-area-delivery]', fmt.format(delivery / 100));
      setText('[data-sk-area-total]', fmt.format(total / 100));
      setText('[data-sk-area-eff]', order > 0 ? fmt.format(total / order / 100) : '–');
      if (minNote) minNote.hidden = !(a > 0 && need < minM2);
      if (qty) qty.value = String(Math.max(1, order));
      btn.disabled = order < 1;
      if (btnText) btnText.textContent = (btn.getAttribute('data-label') || '').replace('%N%', String(order));
    }

    /* Liefertermine: ab heute + Vorlauf (Werktage Mo–Fr), nur Liefertage/-monate des Lieferanten, max. 12 Termine */
    function buildDates() {
      if (!dateSel) return;
      const out = [];
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      let work = 0;
      while (work < lead) { d.setDate(d.getDate() + 1); const wd = d.getDay(); if (wd !== 0 && wd !== 6) work++; }
      let df;
      try { df = new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }); } catch (e) { df = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }); }
      for (let i = 0; i < 200 && out.length < 12; i++) {
        const iso = d.getDay() === 0 ? 7 : d.getDay();
        if ((!days.length || days.indexOf(iso) > -1) && (!months.length || months.indexOf(d.getMonth() + 1) > -1)) {
          const y = d.getFullYear();
          const mo = String(d.getMonth() + 1).padStart(2, '0');
          const da = String(d.getDate()).padStart(2, '0');
          out.push({ v: y + '-' + mo + '-' + da, t: df.format(d) });
        }
        d.setDate(d.getDate() + 1);
      }
      if (!out.length) {
        dateSel.disabled = true;
        if (dateSel.options[0]) dateSel.options[0].textContent = i18n.no_dates || '';
        return;
      }
      for (const o of out) {
        const opt = document.createElement('option');
        opt.value = o.v;
        opt.textContent = o.t;
        dateSel.appendChild(opt);
      }
      dateSel.selectedIndex = 1;
    }

    function say(text, isError) {
      if (!msg) return;
      msg.textContent = text;
      msg.hidden = !text;
      msg.classList.toggle('is-error', !!isError);
      msg.setAttribute('role', isError ? 'alert' : 'status');
    }

    m2.addEventListener('input', update);
    m2.addEventListener('change', function () { if (areaValue() <= 0) m2.value = '1'; update(); });
    if (wasteEl) wasteEl.addEventListener('change', update);
    for (const st of root.querySelectorAll('[data-sk-area-step]')) {
      st.addEventListener('click', function () {
        const step = parseFloat(st.getAttribute('data-sk-area-step')) || 0;
        m2.value = String(Math.min(5000, Math.max(1, Math.round(areaValue() + step))));
        update();
      });
    }
    function fromDims() {
      const lv = parseFloat(String(l.value).replace(',', '.'));
      const bv = parseFloat(String(b.value).replace(',', '.'));
      if (isFinite(lv) && isFinite(bv) && lv > 0 && bv > 0) {
        m2.value = String(Math.min(5000, Math.round(lv * bv * 10) / 10));
        update();
      }
    }
    if (l && b) { l.addEventListener('input', fromDims); b.addEventListener('input', fromDims); }
    if (plz) plz.addEventListener('input', function () { plz.value = plz.value.replace(/\D/g, '').slice(0, 5); plz.removeAttribute('aria-invalid'); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      say('', false);
      if (plz && plz.value && !/^\d{5}$/.test(plz.value)) {
        plz.setAttribute('aria-invalid', 'true');
        plz.focus();
        say(i18n.plz_invalid || '', true);
        return;
      }
      if (dateSel && !dateSel.disabled && !dateSel.value) { dateSel.focus(); say(i18n.date_required || '', true); return; }
      if (state.order < 1) return;
      const props = {};
      if (dateSel && dateSel.value) props[i18n.prop_date || 'Date'] = dateSel.options[dateSel.selectedIndex].textContent;
      const aTxt = nf.format(state.area);
      props[i18n.prop_area || 'Area'] = wasteEl && wasteEl.checked
        ? (i18n.prop_area_val || '').replace('%A%', aTxt).replace('%P%', String(wastePct))
        : (i18n.prop_area_val_nowaste || '').replace('%A%', aTxt);
      if (plz && plz.value) props._PLZ = plz.value;
      btn.disabled = true;
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: state.order, properties: props }] })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          if (!r.ok) {
            const err = new Error('cart-add ' + r.status);
            err.status = r.status;
            err.description = data && (data.description || data.message);
            throw err;
          }
          return fetch(rootUrl + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function (c) { return c.ok ? c.json() : null; })
            .catch(function () { return null; });
        });
      }).then(function (cart) {
        btn.disabled = false;
        if (cart) window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
        let opened = false;
        if (window.skDrawer && typeof window.skDrawer.open === 'function') {
          try { window.skDrawer.open(); opened = true; } catch (e) { opened = false; }
        }
        if (!opened) say((i18n.added || '').replace('%N%', String(state.order)), false);
      }).catch(function (err) {
        btn.disabled = false;
        let t = i18n.error_generic;
        if (!err || err.status == null) t = i18n.error_network;
        else if (err.status === 429) t = i18n.error_rate;
        else if (err.status === 422 && err.description) t = String(err.description);
        say(t || '', true);
      });
    });
    buildDates();
    update();
  }

  /* ---------- 9) Variantenbild: Lieferanten-Foto ↔ KI-Standardbild ---------- */
  function initVariantMedia() {
    const src = document.querySelector('[data-sk-plant-media]');
    if (!src) {
      initVariantImageSimple();
      return;
    }
    let data;
    try {
      data = JSON.parse(src.textContent);
    } catch (e) {
      return;
    }
    const map = (data && data.v) || {};
    function figure() {
      const viewer = document.querySelector('[data-gallery-viewer]');
      if (!viewer) return null;
      return viewer.querySelector('.product-gallery--media-variant-viewer') || viewer.querySelector('figure[data-gallery-index="0"]');
    }
    const fig0 = figure();
    let shown = fig0 ? String(fig0.getAttribute('data-media') || '') : '';
    // Kennzeichnung vorn: KI-Bild → KI-Label (EU-KI-Verordnung), sonst nichts
    function label(fig, d) {
      for (const el of fig.querySelectorAll('.sk-ai-label')) el.remove();
      if (!d.ai || !data.ai) return;
      const span = document.createElement('span');
      span.className = 'sk-ai-label';
      span.textContent = data.ai;
      fig.appendChild(span);
    }
    // Lieferantenfotos: feste Zusatzfolien (Liquid, snippets/sk-plant-media). Knopf auf dem KI-Bild → Foto der
    // gewählten Größe; Zurück-Knopf auf der Folie. Navigation über die (ggf. versteckten) Vorschaubilder wie
    // sk-gallery-nav.js – Empire wählt damit die Folie, auch per Flickity (Mobil).
    const viewerEl = document.querySelector('[data-gallery-viewer]');
    const slides = viewerEl ? Array.from(viewerEl.querySelectorAll('[data-sk-supplier-slide]')) : [];
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    let current = idInput ? String(idInput.value || '') : '';
    function forVariant(slide, id) {
      return (slide.getAttribute('data-sk-variants') || '').split(',').indexOf(String(id)) !== -1;
    }
    function goToIndex(idx) {
      if (idx == null) return;
      const t = document.querySelector('[data-gallery-thumbnail][data-gallery-index="' + idx + '"]');
      if (t) t.click();
    }
    if (slides.length && data.note) {
      const hero = figure();
      if (hero && !hero.querySelector('.sk-plant-photo-jump')) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sk-plant-photo-jump';
        btn.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 8h3l1.6-2h6.8L17 8h3v11H4z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="12" cy="13.2" r="3.4" fill="none" stroke="currentColor" stroke-width="1.8"/></svg><span></span>';
        btn.querySelector('span').textContent = data.note;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          const target = slides.find(function (sl) { return forVariant(sl, current); }) || slides[0];
          goToIndex(target.getAttribute('data-gallery-index'));
        });
        hero.appendChild(btn);
      }
      for (const sl of slides) {
        const back = sl.querySelector('[data-sk-supplier-back]');
        if (!back) continue;
        back.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          const f = figure();
          if (f) goToIndex(f.getAttribute('data-gallery-index'));
        });
      }
    }
    let seq = 0;
    function apply(id, navigate) {
      const d = map[String(id)];
      if (!d) return;
      const fig = figure();
      if (!fig) return;
      const changed = shown !== String(d.m);
      const token = ++seq;
      // Bild, Zoom, Vorschaubild und Kennzeichnung erst tauschen, wenn das neue Bild geladen ist – nie eine leere Folie
      const swap = function () {
        if (token !== seq) return;
        const img = fig.querySelector('img.product-gallery--loaded-image') || fig.querySelector('img');
        if (img) {
          if (img.getAttribute('src') !== d.src) {
            img.removeAttribute('data-rimg-template');
            img.srcset = d.srcset;
            img.src = d.src;
          }
          img.alt = d.alt || '';
        }
        const bg = fig.querySelector('.sk-plant-media__bg');
        if (bg && d.bg && bg.getAttribute('src') !== d.bg) bg.src = d.bg;
        fig.setAttribute('data-zoom', d.zoom);
        fig.setAttribute('data-image-width', String(d.zw));
        fig.setAttribute('data-image-height', String(d.zh));
        const thumb = document.querySelector('.media-variant-thumbnail img');
        if (thumb) {
          thumb.srcset = d.thumb;
          thumb.src = d.thumb;
          thumb.alt = d.alt || '';
        }
        label(fig, d);
      };
      const cur = fig.querySelector('img.product-gallery--loaded-image') || fig.querySelector('img');
      if (cur && cur.getAttribute('src') !== d.src) {
        const pre = new Image();
        pre.srcset = d.srcset;
        pre.src = d.src;
        // decode(): erst tauschen, wenn das Bild dekodiert ist (sonst kurz leere Folie); Fallback onload
        if (typeof pre.decode === 'function') pre.decode().then(swap, swap);
        else {
          pre.onload = swap;
          pre.onerror = swap;
          if (pre.complete && pre.naturalWidth) swap();
        }
      } else {
        swap();
      }
      shown = String(d.m);
      current = String(id);
      // Gerade sichtbar: Lieferantenfoto einer anderen Größe → zurück zum KI-Bild
      const sel = viewerEl && viewerEl.querySelector('figure[data-gallery-selected="true"]');
      if (navigate && sel && sel.hasAttribute('data-sk-supplier-slide') && !forVariant(sel, id)) {
        goToIndex(fig.getAttribute('data-gallery-index'));
        return;
      }
      // Bild hat gewechselt und die Folie ist gerade nicht sichtbar → hinblättern (wie Empire beim Variantenwechsel)
      if (navigate && changed && fig.getAttribute('aria-hidden') === 'true') {
        const btn = document.querySelector('.media-variant-thumbnail');
        if (btn) btn.click();
      }
    }
    // nach Empire (das tauscht synchron auf das Originalbild) → eigene, passend skalierte Bilder + Kennzeichnung
    const later = function (id) { window.setTimeout(function () { apply(id, true); }, 0); };
    document.addEventListener('sk-plant:variant', function (e) {
      if (e.detail && e.detail.id) later(e.detail.id);
    });
    for (const vs of document.querySelectorAll('variant-selection')) {
      vs.addEventListener('variant-change', function (e) {
        const v = e.detail && e.detail.variant;
        if (v && v.id) later(v.id);
      });
    }
    document.addEventListener('change', function (e) {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || t.type !== 'radio' || !t.matches('[data-variant-option-value]') || !t.checked) return;
      const vid = t.getAttribute('data-variant-id') || (variants[t.value] && variants[t.value].id);
      if (vid) later(vid);
    });
  }

  /* ---------- 9b) Variantenbild ohne Lieferantenfotos (29.10.2026) ----------
   * Jede Pflanzen-Größe hat ein eigenes Bild (variant.featured_media, u. a. für Google Shopping). Empire tauscht das
   * Bild der Varianten-Folie hier nicht (kein Galerie-Objekt) → beim Größenwechsel (Mengen-View, Zeilenklick, Empire)
   * Bild, Zoom, Hintergrund und Vorschaubild selbst tauschen und zur Folie blättern. Daten: <variant-selection>.getProduct(). */
  function initVariantImageSimple() {
    const viewer = document.querySelector('[data-gallery-viewer]');
    const fig = viewer && viewer.querySelector('.product-gallery--media-variant-viewer');
    const vs = document.querySelector('variant-selection');
    if (!fig) return;
    // <variant-selection> wird ggf. erst nach uns definiert → Produktdaten erst beim Wechsel holen, sonst /products/<handle>.js
    let cache = null;
    function getProduct() {
      if (vs && typeof vs.getProduct === 'function') return vs.getProduct();
      if (!cache) {
        cache = fetch(window.location.pathname.replace(/\/$/, '') + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }
      return cache;
    }
    const sized = function (u, w) {
      if (!u) return '';
      return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    };
    let shown = String(fig.getAttribute('data-media') || '');
    let seq = 0;
    function apply(id) {
      const token = ++seq;
      getProduct().then(function (product) {
        if (token !== seq || !product || !product.variants) return;
        const v = product.variants.find(function (x) { return String(x.id) === String(id); });
        const m = v && v.featured_media;
        const fi = v && (v.featured_image || (m && m.preview_image));
        const s = fi && (fi.src || fi.url);
        if (!m || !s || String(m.id) === shown) return;
        const pre = new Image();
        pre.src = sized(s, 700);
        const swap = function () {
          if (token !== seq) return;
          const img = fig.querySelector('img.product-gallery--loaded-image') || fig.querySelector('img');
          if (img) {
            img.removeAttribute('data-rimg-template');
            img.srcset = sized(s, 700) + ' 1x, ' + sized(s, 1400) + ' 2x';
            img.src = sized(s, 700);
            img.alt = (v.featured_image && v.featured_image.alt) || (m && m.alt) || img.alt;
            img.setAttribute('data-zoom', sized(s, 2000));
          }
          const bg = fig.querySelector('.sk-plant-media__bg');
          if (bg) bg.src = sized(s, 64);
          fig.setAttribute('data-zoom', sized(s, 2000));
          fig.setAttribute('data-media', String(m.id));
          const thumb = document.querySelector('.media-variant-thumbnail img');
          if (thumb) {
            thumb.srcset = sized(s, 150);
            thumb.src = sized(s, 150);
          }
          shown = String(m.id);
          // Empire/rimg setzt das Bild beim Nachladen teils zurück (Tablet) → einmal nachprüfen
          window.setTimeout(function () {
            const im = fig.querySelector('img.product-gallery--loaded-image');
            if (token === seq && im && (im.currentSrc || im.src).indexOf(s.split('?')[0].split('/').pop()) === -1) {
              im.removeAttribute('data-rimg-template');
              im.srcset = sized(s, 700) + ' 1x, ' + sized(s, 1400) + ' 2x';
              im.src = sized(s, 700);
            }
          }, 1200);
          if (fig.getAttribute('aria-hidden') === 'true') {
            const btn = document.querySelector('.media-variant-thumbnail');
            if (btn) btn.click();
          }
        };
        if (typeof pre.decode === 'function') pre.decode().then(swap, swap);
        else { pre.onload = swap; pre.onerror = swap; }
      }).catch(function () { /* Bild bleibt */ });
    }
    document.addEventListener('sk-plant:variant', function (e) {
      if (e.detail && e.detail.id) apply(e.detail.id);
    });
    if (vs) {
      vs.addEventListener('variant-change', function (e) {
        const v = e.detail && e.detail.variant;
        if (v && v.id) apply(v.id);
      });
    }
  }

  /* ---------- Auf-/Zuklappen (21.10.2026) ---------- */
  function swapLabel(btn, open) {
    const lbl = btn.querySelector('[data-sk-more-label]');
    if (!lbl) return;
    if (!btn.hasAttribute('data-more')) btn.setAttribute('data-more', lbl.textContent);
    lbl.textContent = open ? (btn.getAttribute('data-less') || lbl.textContent) : btn.getAttribute('data-more');
  }
  function initToggles() {
    // Steckbrief: weitere Eigenschaften
    for (const btn of document.querySelectorAll('[data-sk-more]')) {
      const target = document.getElementById(btn.getAttribute('data-sk-more'));
      if (!target) continue;
      btn.addEventListener('click', function () {
        const open = !target.classList.contains('is-all');
        target.classList.toggle('is-all', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        swapLabel(btn, open);
      });
    }
    // Beschreibung: Weiterlesen (nur wenn der Text wirklich länger ist)
    for (const btn of document.querySelectorAll('[data-sk-clamp-btn]')) {
      const target = document.getElementById(btn.getAttribute('data-sk-clamp-btn'));
      if (!target) continue;
      if (target.scrollHeight <= target.clientHeight + 24) {
        target.classList.add('is-open', 'is-short');
        btn.hidden = true;
        continue;
      }
      btn.addEventListener('click', function () {
        const open = !target.classList.contains('is-open');
        target.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        swapLabel(btn, open);
        if (!open) {
          const top = target.getBoundingClientRect().top;
          if (top < 0) target.scrollIntoView({ block: 'start' });
        }
      });
    }
    // Mobile Aufklapper der Sections (Pflanzanleitung, Pflegekarten, Schädlinge)
    for (const btn of document.querySelectorAll('[data-sk-macc-btn]')) {
      const box = btn.closest('[data-sk-macc]');
      if (!box) continue;
      btn.addEventListener('click', function () {
        const open = !box.classList.contains('is-open');
        box.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        swapLabel(btn, open);
      });
    }
    // Links auf den Heckenrechner (Aufklapper an der Größenliste) öffnen ihn
    const calc = document.querySelector('details#sk-plant-hedge');
    if (calc) {
      document.addEventListener('click', function (e) {
        const a = e.target.closest && e.target.closest('a[href="#sk-plant-hedge"]');
        if (!a) return;
        e.preventDefault();
        calc.open = true;
        calc.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      if (window.location.hash === '#sk-plant-hedge') calc.open = true;
    }
  }

  function init() {
    initToggles();
    initVariantSync();
    initVariantMedia();
    for (const root of document.querySelectorAll('[data-sk-plant-hedge]')) initHedge(root);
    for (const root of document.querySelectorAll('[data-sk-calc]')) initCalc(root);
    for (const root of document.querySelectorAll('[data-sk-site]')) initSite(root);
    for (const root of document.querySelectorAll('[data-sk-tree-check]')) initTreeCheck(root);
    for (const root of document.querySelectorAll('[data-sk-area]')) initArea(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
